import { useCallback, useEffect, useReducer, useRef, useState } from "react"
import { createRoot } from "react-dom/client"
import {
  attachInstruction,
  extractInstruction,
  Instruction as DndInstruction,
  Operation as DndOperation,
} from "@atlaskit/pragmatic-drag-and-drop-hitbox/list-item"
import { combine } from "@atlaskit/pragmatic-drag-and-drop/combine"
import {
  draggable,
  dropTargetForElements,
  ElementDropTargetEventBasePayload,
  monitorForElements,
} from "@atlaskit/pragmatic-drag-and-drop/element/adapter"
import { setCustomNativeDragPreview } from "@atlaskit/pragmatic-drag-and-drop/element/set-custom-native-drag-preview"
import { pointerOutsideOfPreview } from "@atlaskit/pragmatic-drag-and-drop/element/pointer-outside-of-preview"
import { DropIndicator as DndDropIndicator } from "@atlaskit/pragmatic-drag-and-drop-react-drop-indicator/list-item"
import { useLatest } from "../../hooks/use-latest"
import { requireNonNull } from "../../helpers/require-non-null"
import { Signal } from "../../utils/signals"

/**
 * Hooks to add D&D behaviour to any tree component.
 *
 * Based on patterns from Atlassian's official examples:
 * https://github.com/atlassian/pragmatic-drag-and-drop/blob/eecf011f/packages/documentation/examples/tree.tsx
 */

export type { DndInstruction, DndOperation }
export { DndDropIndicator }

type TreeRootDndArgs = {
  /** Whether to enable drag&drop functionality */
  enabled: boolean
  /** Unique ID for the tree. Can only drag/drop within same treeId. */
  treeId: string
  /** Ref for the DOM element surrounding the root tree nodes */
  rootRef: React.RefObject<HTMLElement>
  /**
   * Handler for drop operations or attempts.
   * Blocked operations need to be handled by the consumer, e.g. by showing
   * a toast and canceling the operation.
   */
  onDrop?: (instruction: DndInstruction, source: string, target: string) => void
}

export function useTreeRootDnd(args: TreeRootDndArgs) {
  const [ state, setState ] = useState<"idle" | "is-innermost-over">("idle")
  const [ dep, update ] = useEffectTrigger()
  const latestOnDrop = useLatest(args.onDrop)

  useEffect(() => {
    if (!args.enabled) return
    const root = requireNonNull(args.rootRef.current)

    function onDropTargetChange({ location, self }: ElementDropTargetEventBasePayload) {
      const [ innerMost ] = location.current.dropTargets.filter(
        (dropTarget) => dropTarget.data.type === "group",
      )

      setState(innerMost?.element === self.element ? "is-innermost-over" : "idle");
    }

    return combine(
      monitorForElements({
        canMonitor: ({ source }) =>
          source.data.uniqueContextId === args.treeId &&
          source.data.type === "tree-item",
        onDrop(payload) {
          const { location, source } = payload
          if (!location.current.dropTargets.length) return

          const target = location.current.dropTargets[0]
          const instruction: DndInstruction | null = extractInstruction(target.data)
          if (instruction !== null) {
            latestOnDrop.current?.(instruction, source.data.id as string, target.data.id as string)
          }
        },
      }),
      dropTargetForElements({
        element: root,
        canDrop: ({ source }) =>
          source.data.uniqueContextId === args.treeId &&
          source.data.type === "tree-item",
        getData: () => ({ type: "group" }),
        onDragStart: onDropTargetChange,
        onDropTargetChange: onDropTargetChange,
        onDragLeave: () => setState("idle"),
        onDrop: () => setState("idle"),
      }),
    )
  }, [
    args.enabled,
    args.treeId,
    dep
  ])

  return [ state, update ] as const
}

type TreeNodeDndArgs = {
  /** Whether to enable drag&drop functionality */
  enabled: boolean
  /** Unique ID for the tree. Can only drag/drop within same treeId. */
  treeId: string
  /** Unique ID for the node */
  nodeId: string
  /** Enable drag&drop to change order */
  reorderFeatureEnabled: boolean
  /** Enable drag&drop for moving items in hierachy (reparenting) */
  reparentFeatureEnabled: boolean
  /** Should return whether source can be dropped or reordered over the target  */
  isValidDropTarget: (operation: DndOperation, sourceId: string, targetId: string) => boolean
  /** Is this node currently expanded? Affects auto-expand and ordering functionality.  */
  isNodeExpanded: boolean
  /** Is this node expandable? Affects auto-expand functionality. */
  isNodeExpandable: boolean
  /** Callback to set node expanded */
  setNodeExpanded: (expanded: boolean) => void
  /** Should return the latest DOM element for the draggable tree node row */
  getRow: () => (HTMLElement | null | undefined)
  /**
   * Should return the latest DOM element for the group surrounding the tree node,
   * which includes its children
   */
  getGroup: () => (HTMLElement | null | undefined)
  /** Should return the latest DOM element for the drag handle */
  getDragHandle: () => (HTMLElement | null | undefined)
  /**
   * Render the drag&drop drag preview.
   * IMPORTANT: This is rendered in a new ReactDOM root;
   *            rendered items will not have access to current React context(s)
   */
  renderPreview: () => React.ReactNode
}

type NodeDndState = {
  node: "idle" | "dragging" | "preview"
  group: "is-innermost-over" | "idle"
  instruction: DndInstruction | null
}

export function useTreeNodeDnd(args: TreeNodeDndArgs) {
  const [ state, setState ] = useState<NodeDndState>({
    node: "idle",
    group: "idle",
    instruction: null
  })
  const [ dep, update ] = useEffectTrigger()
  const [ cancelExpandRef, cancelExpand ] = useAutoCancelableFn()
  const latestArgs = useLatest(args)

  useEffect(() => {
    if (!args.enabled) return
    const row = requireNonNull(args.getRow())
    const dragHandle = requireNonNull(args.getDragHandle())

    function onChange({ self }: ElementDropTargetEventBasePayload) {
      const instruction = extractInstruction(self.data)

      // expand after 1000ms if still merging
      if (
        instruction?.operation === "combine" &&
        args.isNodeExpandable &&
        !cancelExpandRef.current
      ) {
        cancelExpandRef.current = delay({
          waitMs: 1000,
          fn: () => args.setNodeExpanded(true)
        })
      }
      if (instruction?.operation !== "combine" && cancelExpandRef.current) {
        cancelExpand()
      }

      setState(s => ({ ...s, instruction }))
    }

    return combine(
      draggable({
        element: dragHandle,
        getInitialData: () => ({
          id: args.nodeId,
          type: 'tree-item',
          isOpenOnDragStart: args.isNodeExpandable,
          uniqueContextId: args.treeId,
        }),
        onGenerateDragPreview: ({ nativeSetDragImage }) => {
          setCustomNativeDragPreview({
            getOffset: pointerOutsideOfPreview({ x: "16px", y: "8px" }),
            render: ({ container }) => {
              const root = createRoot(container)
              root.render(latestArgs.current.renderPreview())
              return () => root.unmount()
            },
            nativeSetDragImage,
          });
        },
        onDragStart: ({ source }) => {
          setState(s => ({ ...s, node: "dragging" }))
          // collapse open items during a drag
          if (!source.data.isOpenOnDragStart) return
          args.setNodeExpanded(false)
        },
        onDrop: ({ source }) => {
          setState(s => ({ ...s, node: "idle" }))
          if (!source.data.isOpenOnDragStart) return
          args.setNodeExpanded(true)
        },
      }),
      dropTargetForElements({
        element: row,
        getData: ({ input, source, element }) => {
          const data = { id: args.nodeId }
          const sourceId = source.data.id as string

          const canDrop = latestArgs.current.isValidDropTarget("combine", sourceId, args.nodeId)
          const canReorderBefore = latestArgs.current.isValidDropTarget("reorder-before", sourceId, args.nodeId)
          const canReorderAfter = latestArgs.current.isValidDropTarget("reorder-after", sourceId, args.nodeId)

          return attachInstruction(data, {
            input,
            element,
            operations: {
              "combine": args.reparentFeatureEnabled
                ? canDrop ? "available" : "blocked"
                : "not-available",
              "reorder-before": args.reorderFeatureEnabled
                ? (canReorderBefore ? "available" : "blocked")
                : "not-available",
              // Don't allow 'reorder-after' on expanded items
              "reorder-after":
                args.reorderFeatureEnabled && !(args.isNodeExpanded && args.isNodeExpandable)
                  ? (canReorderAfter ? "available" : "blocked")
                  : "not-available"
            },
          });
        },
        canDrop: ({ source }) =>
          source.data.type === 'tree-item' &&
          source.data.id !== args.nodeId &&
          source.data.uniqueContextId === args.treeId,
        onDragEnter: onChange,
        onDrag: onChange,
        onDragLeave: () => {
          cancelExpand()
          setState(s => ({ ...s, instruction: null }))
        },
        onDrop: () => {
          cancelExpand()
          setState(s => ({ ...s, instruction: null }))
        },
      }),
    )
  }, [
    setState,
    args.enabled,
    args.treeId,
    args.nodeId,
    args.reorderFeatureEnabled,
    args.reparentFeatureEnabled,
    args.isNodeExpanded,
    args.isNodeExpandable,
    dep
  ])

  useEffect(() => {
    if (!args.enabled) return
    const group = args.getGroup()
    if (!group) return // item has no children or is not open

    function onChange({ location, self }: ElementDropTargetEventBasePayload) {
      const [ innerMost ] = location.current.dropTargets.filter(
        (dropTarget) => dropTarget.data.type === "group",
      )
      setState(s => ({
        ...s, group: innerMost?.element === self.element ? "is-innermost-over" : "idle"
      }))
    }

    return dropTargetForElements({
      element: group,
      canDrop: ({ source }) => {
        return source.data.type === 'tree-item' &&
          source.data.id !== args.nodeId &&
          source.data.uniqueContextId === args.treeId
      },
      getData: () => ({ type: 'group' }),
      getIsSticky: () => false,
      onDragStart: onChange,
      onDropTargetChange: onChange,
      onDragLeave: () => setState(s => ({ ...s, group: "idle" })),
      onDrop: () => setState(s => ({ ...s, group: "idle" }))
    });
  }, [
    setState,
    args.enabled,
    args.treeId,
    args.nodeId,
    dep
  ])

  return [ state, update ] as const
}

export function DndGroupDropIndicator(props: {
  isActive: boolean
  leftPadding?: number
}) {
  if (!props.isActive) return null
  const leftPadding = props.leftPadding ?? 0
  return (
    <div className="
              absolute
              w-full h-full
              pointer-events-none z-10
              inset-0 rounded-md
              bg-blue-500 bg-opacity-10
              ring-2 ring-inset ring-blue-500" style={{
      top: '-4px',
      left: `calc(-4px + ${leftPadding.toFixed(1)}px)`,
      height: "calc(100% + 8px)",
      width: `calc(100% + (8px - ${leftPadding.toFixed(1)}px) - 4px)`,
    }} />
  )
}

/** Helper to "poke" an effect to trigger again imperatively */
function useEffectTrigger() {
  const signal = useRef(new Signal<void>())
  const [ counter, bump ] = useReducer((count: number) => count + 1, 0)
  useEffect(() => {
    signal.current.connect(bump)
    return () => signal.current.disconnect(bump)
  }, [])
  const trigger = useCallback(() => signal.current.emit(), [])
  return [ counter, trigger ] as const
}

function useAutoCancelableFn() {
  const cancelableRef = useRef<(() => void) | null>(null)
  const cancel = useCallback(() => {
    cancelableRef.current?.()
    cancelableRef.current = null
  }, [])

  useEffect(() => {
    return () => cancel()
  }, [ cancel ])

  return [ cancelableRef, cancel ] as const
}

function delay({ waitMs: timeMs, fn }: { waitMs: number; fn: () => void }): () => void {
  let timeoutId: number | null = window.setTimeout(() => {
    timeoutId = null
    fn();
  }, timeMs)
  return function cancel() {
    if (timeoutId) {
      window.clearTimeout(timeoutId)
      timeoutId = null
    }
  };
}
